import { useState } from "react";
import type { ReactElement } from "react";
import type { LiquiditySeries, Unternehmen } from "@kundeninfo/api-client";
import { Chip, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { ObjektVerweis } from "../../components/objektPanel/ObjektVerweis.js";
import { monatBeschriftung } from "../tickets/ticketFormat.js";
import { shortDay } from "../tickets/timelineTexts.js";
import { euro, hints, tableRows } from "./liquidityModel.js";
import type { DetailWindow, Hint, TableRow } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

type PeriodRow = Extract<TableRow, { kind: "PERIOD" }>;
type Column = { key: string; head: string; cell: (r: PeriodRow) => string };

const CLOSING: Column = { key: "closing", head: T.table.closing, cell: (r) => r.closingEuro };
const LOWEST: Column = { key: "lowest", head: T.table.lowest, cell: (r) => T.table.lowestAt(r.minEuro, r.minDate) };
const FLOWS: readonly Column[] = [
  { key: "in", head: T.table.in, cell: (r) => r.inEuro }, { key: "out", head: T.table.out, cell: (r) => r.outEuro },
  { key: "net", head: T.table.net, cell: (r) => r.netEuro },
];
const COMPARE: readonly Column[] = [
  { key: "compare", head: T.table.compareClosing, cell: (r) => r.compareClosingEuro ?? "" },
  { key: "delta", head: T.table.delta, cell: (r) => r.deltaEuro ?? "" },
];
const CLOSING_AT_END: Column = { ...CLOSING, head: T.table.closingAtPeriodEnd };
const CHANGE: Column = { key: "change", head: T.table.change, cell: (r) => r.changeEuro ?? "" };

/** Die Spalten je Verdichtung (Spec „Tabelle“); am Telefon stehen Stand am Ende und Tiefststand vorn (Spec „Telefon“). */
function columnsOf(s: LiquiditySeries, phone: boolean): readonly Column[] {
  const compare = s.compare === null ? [] : COMPARE;
  if (s.granularity === "MONTH_END") return phone ? [CLOSING_AT_END, LOWEST, CHANGE, ...compare] : [CLOSING_AT_END, CHANGE, LOWEST, ...compare];
  return phone ? [CLOSING, LOWEST, ...FLOWS, ...compare] : [...FLOWS, CLOSING, LOWEST, ...compare];
}

/**
 * DIE TABELLE (Spec „Tabelle“): die zugängliche Fassung des Diagramms, `me-tabelle` mit klebendem Kopf, am Telefon gestapelt. Zeilen
 * rechnet `tableRows`: die Perioden des sichtbaren Fensters, danach Quartale und Jahre, je Zone eine Zwischenzeile; bei „5 Jahre“ zuerst
 * der Ausgangsstand (H10). Eine Periode unter dem Mindestsaldo trägt das Kennzeichen als Fläche in Fehlerfarbe.
 */
export function LiquidityTable(p: { series: LiquiditySeries; window: DetailWindow; phone: boolean }): ReactElement {
  const columns = columnsOf(p.series, p.phone);
  const span = columns.length + 1;
  return (
    <Tabelle stapeln dicht className="liq__table">
      {/* Ab 744 px rollt die Tabelle selbst; WebKit macht einen Rollbereich nicht von sich aus per Tastatur erreichbar. Der Name steht an der
          Tabelle selbst: An `me-tabelle` ohne Rolle sagt ihn niemand an (gemessen, Aufgabe 12). */}
      <table aria-label={T.table.label} {...(p.phone ? {} : { tabIndex: 0 })}>
        <thead>
          <tr>
            <th scope="col">{T.table.period}</th>
            {columns.map((c) => <th key={c.key} scope="col" data-ausrichtung="rechts">{c.head}</th>)}
          </tr>
        </thead>
        <tbody>
          {tableRows(p.series, p.window).map((r) => {
            if (r.kind === "ZONE") return <tr key={r.zone} className="liq__zone-row"><th colSpan={span} scope="rowgroup" data-label="">{r.label}</th></tr>;
            if (r.kind === "ANCHOR") {
              return (
                <tr key={r.label} className="liq__anchor-row">
                  <th scope="row">{r.label}</th>
                  {columns.map((c) => <td key={c.key} data-ausrichtung="rechts">{c.key === CLOSING.key ? r.closingEuro : ""}</td>)}
                </tr>
              );
            }
            return (
              <tr key={r.key} data-period={r.key}>
                <th scope="row">
                  <span className="liq__period">
                    <span className="liq__period-label">{r.label}</span>
                    {r.belowMinimum && <Chip statusArt="fehler">{T.table.belowMinimum}</Chip>}
                  </span>
                </th>
                {columns.map((c) => <td key={c.key} data-ausrichtung="rechts">{c.cell(r)}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Tabelle>
  );
}

type VatMonth = NonNullable<LiquiditySeries["warnings"]["vatHint"]>[number];

/** Ein Überhang ist keine Zahllast und hat keine Fälligkeit (Ruling): eigener Satz, Betrag ohne Vorzeichen. */
const vatLine = (m: VatMonth): string => {
  const month = monatBeschriftung(`${m.month}-01`);
  return m.cents < 0 ? T.vat.surplus(month, euro(-m.cents)) : T.vat.line(month, euro(m.cents), shortDay(m.dueDate));
};

/** Die Zeile des nächsten Monats: unter der Tabelle und im Panel einer Planposition `TAX`. `null` ohne Wert und bei mehreren Unternehmen. */
export function nextVatLine(s: LiquiditySeries): string | null {
  const first = s.warnings.vatHint?.[0];
  if (first === undefined || s.companyIds.length !== 1) return null;
  return s.positions.some((x) => x.enabled && x.frozen !== null) ? T.vat.withoutFrozen(vatLine(first)) : vatLine(first);
}

/**
 * DER USt-HINWEIS (Spec „USt-Hinweis“, A7): eine Zeile unter der Tabelle, nie eine Fläche; der nächste Monat steht da, die übrigen klappen auf.
 * Nur bei genau einem Unternehmen: Jedes meldet selbst, die Zahllast mehrerer wird nicht verrechnet. Der Vertrag ist nullbar, `null` und `[]`
 * heißen dasselbe: keine Zeile. Eingefrorene Positionen zählen nicht, der Schnappschuss kennt keine Steuer; das sagt die Zeile dann auch.
 */
export function LiquidityVatHint(p: { series: LiquiditySeries }): ReactElement | null {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = p.series.warnings.vatHint ?? [];
  if (first === undefined) return null;
  if (p.series.companyIds.length !== 1) return <p className="liq__muted liq__vat">{T.vat.perCompany}</p>;
  return (
    <Hinweis art="info" className="liq__vat">
      <div className="liq__vat-lines">
        <span>{nextVatLine(p.series)}</span>
        {rest.length > 0 && <KiKnopf art="leise" kompakt ausgeklappt={open} onClick={() => setOpen((o) => !o)}>{T.vat.more(rest.length)}</KiKnopf>}
        {open && <ul className="liq__hint-list">{rest.map((m) => <li key={m.month}>{vatLine(m)}</li>)}</ul>}
      </div>
    </Hinweis>
  );
}

const ERROR_HINTS: readonly Hint["kind"][] = ["BELOW_MINIMUM", "MISSING"];

/**
 * DIE HINWEISZEILE (Spec „Tabelle“): das Wichtigste zuerst (`hints`), die Art nach dem Schlechtesten: Unterschreitung oder fehlende
 * Quelle sind ein Fehler, nur der Tiefststand ist eine Auskunft. „Aufträge ohne Zahlplan“ klappt auf; jeder Auftrag ist ein fremdes
 * Objekt (K4, 115): Name als Text, der Verweis direkt dahinter (Sprung in die Akte, Panel ab Stufe 3). Ohne Basis eines Unternehmens
 * (P6) steht hier, warum „Mindestsaldo …“ gesperrt ist.
 */
export function LiquidityHints(p: { series: LiquiditySeries; accounts: readonly { id: string; name: string }[];
  companies: readonly Unternehmen[]; onGeaendert: () => void }): ReactElement {
  const [ordersOpen, setOrdersOpen] = useState(false);
  const s = p.series;
  const list = hints(s, p.accounts, s.companyIds.length > 1 ? p.companies : []);
  const noBaseline = s.scenario.id === null && s.companyIds.length === 1;
  const art = list.some((h) => ERROR_HINTS.includes(h.kind)) ? "fehler"
    : noBaseline || list.some((h) => h.kind !== "LOWEST") ? "warnung" : "info";
  return (
    <Hinweis art={art} className="liq__hints" aria-label={T.hintsName}>
      <ul className="liq__hint-list">
        {list.map((h) => (
          <li key={`${h.kind}|${h.text}`}>
            {h.kind !== "WITHOUT_PLAN" ? h.text : (
              <>
                <KiKnopf art="leise" kompakt ausgeklappt={ordersOpen} onClick={() => setOrdersOpen((o) => !o)}>{h.text}</KiKnopf>
                {ordersOpen && (
                  <ul className="liq__hint-orders">
                    {s.warnings.ordersWithoutPlan.map((o) => (
                      <li key={`${o.companyId}|${o.orderId}`}>
                        <span>{`${o.reference} ${o.title}`}</span>
                        <ObjektVerweis bezug={{ art: "auftrag", id: o.orderId, customerId: o.customerId, companyId: o.companyId, label: o.reference }}
                          onGeaendert={p.onGeaendert} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </li>
        ))}
        {noBaseline && <li>{T.toolbar.minimumBalanceLocked}</li>}
      </ul>
    </Hinweis>
  );
}
